/**
 * Stream error classification.
 *
 * Centralizes error categorization so retry logic, Sentry reporting,
 * and consumer callbacks all use the same classification.
 *
 * Adapted from the project's existing useStreamingWithCleanup patterns,
 * but decoupled from React so it can be used in workers and non-React code.
 */

import { EStreamErrorType, type IStreamError } from './types';

/** Custom abort reason used to identify intentional stream cancellations. */
export const STREAM_ABORT_REASON = 'STREAM_CANCELLED';

/** Custom abort reason for request timeouts. */
export const STREAM_TIMEOUT_REASON = 'STREAM_TIMEOUT';

/**
 * Browser-specific error messages for mid-stream body aborts.
 * These are recoverable because the server may still be healthy.
 */
const BROWSER_STREAM_ABORT_MESSAGES = [
  'BodyStreamBuffer was aborted', // Chrome
  'Error in input stream', // Firefox
];

/**
 * Browser-specific error messages for pre-response network failures.
 * Also recoverable — DNS, connection refused, offline, page navigation.
 */
const BROWSER_PREFETCH_NETWORK_ERROR_MESSAGES = [
  'Failed to fetch', // Chrome
  'NetworkError when attempting to fetch resource.', // Firefox
  'Load failed', // Safari
];

/** HTTP statuses considered transient and retryable. */
export const RETRYABLE_HTTP_STATUSES = new Set([408, 429, 502, 503, 504]);

/** Default fatal HTTP statuses that should never be retried. */
export const DEFAULT_FATAL_HTTP_STATUSES = new Set([401, 403, 404, 410]);

/**
 * Classifies a stream error into user-initiated, recoverable, or non-recoverable.
 *
 * @param error - The error to classify
 * @param signal - Optional AbortSignal associated with the request
 * @returns Classified stream error
 */
export function classifyStreamError(
  error: unknown,
  signal?: AbortSignal,
): IStreamError {
  const wrappedError =
    error instanceof Error ? error : new Error(String(error));

  // An internal inactivity/request-timeout abort is recoverable, NOT a user
  // cancellation. The timeout fires via `abort(STREAM_TIMEOUT_REASON)`, which
  // sets `signal.aborted`, so this must be checked BEFORE the generic
  // `signal.aborted` branch below — otherwise the timeout is misclassified as
  // USER_INITIATED and connectLoop returns silently (no onError, no resume
  // scheduled), leaving the consumer stuck forever (OC-1310).
  if (
    signal?.reason === STREAM_TIMEOUT_REASON ||
    error === STREAM_TIMEOUT_REASON
  ) {
    return {
      type: EStreamErrorType.RECOVERABLE,
      error: wrappedError,
      reason: 'timeout',
    };
  }

  if (signal?.aborted) {
    return {
      type: EStreamErrorType.USER_INITIATED,
      error: wrappedError,
      reason: 'signal-aborted',
    };
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return {
      type: EStreamErrorType.USER_INITIATED,
      error: wrappedError,
      reason: 'dom-abort-error',
    };
  }

  if (error === STREAM_ABORT_REASON) {
    return {
      type: EStreamErrorType.USER_INITIATED,
      error: new Error(STREAM_ABORT_REASON),
      reason: 'stream-cancelled',
    };
  }

  if (
    error instanceof Error &&
    'cause' in error &&
    error.cause === STREAM_ABORT_REASON
  ) {
    return {
      type: EStreamErrorType.USER_INITIATED,
      error: wrappedError,
      reason: 'stream-cancelled-via-cause',
    };
  }

  if (
    error instanceof Error &&
    error.message === 'Stream parsing was aborted'
  ) {
    const isIntentional =
      'cause' in error && error.cause === STREAM_ABORT_REASON;
    return {
      type: isIntentional
        ? EStreamErrorType.USER_INITIATED
        : EStreamErrorType.RECOVERABLE,
      error: wrappedError,
      reason: isIntentional
        ? 'parser-aborted-intentional'
        : 'parser-aborted-unexpected',
    };
  }

  if (
    error instanceof TypeError &&
    BROWSER_STREAM_ABORT_MESSAGES.includes(error.message)
  ) {
    return {
      type: EStreamErrorType.RECOVERABLE,
      error: wrappedError,
      reason: 'browser-mid-stream-abort',
    };
  }

  if (error instanceof TypeError && error.message === 'network error') {
    return {
      type: EStreamErrorType.RECOVERABLE,
      error: wrappedError,
      reason: 'network-error',
    };
  }

  if (
    error instanceof TypeError &&
    BROWSER_PREFETCH_NETWORK_ERROR_MESSAGES.includes(error.message)
  ) {
    return {
      type: EStreamErrorType.RECOVERABLE,
      error: wrappedError,
      reason: 'pre-response-network-error',
    };
  }

  if (error instanceof Error && error.name === 'TimeoutError') {
    return {
      type: EStreamErrorType.RECOVERABLE,
      error: wrappedError,
      reason: 'timeout',
    };
  }

  return {
    type: EStreamErrorType.NON_RECOVERABLE,
    error: wrappedError,
    reason: 'unknown',
  };
}

/**
 * Checks whether an error was caused by an intentional stream cancellation.
 *
 * @param error - The error to check
 * @param signal - Optional AbortSignal
 * @returns True if the error is from an intentional abort
 */
export function isIntentionalAbort(
  error: unknown,
  signal?: AbortSignal,
): boolean {
  return (
    classifyStreamError(error, signal).type === EStreamErrorType.USER_INITIATED
  );
}
